import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { Button, useCkToast } from "@NP-Webber/crm-kit/dist/next/index.js";
import { useQuickCreate } from "../../next/quickCreate";
import { useSearchParams } from "react-router-dom";
import {
  meetingsApi,
  type AssignableUser,
  type MeetingFull,
} from "../../api/meetingsApi";
import { shellApi } from "../../api/shellApi";
import { toClientApiError } from "../../api/httpClient";
import { ArchivePanel } from "./components/ArchivePanel";
import { DefaultParticipantsDialog } from "./components/DefaultParticipantsDialog";
import { PhaseNav } from "./components/PhaseNav";
import { PreparationPanel } from "./components/PreparationPanel";
import { SummaryPanel } from "./components/SummaryPanel";
import { TeamMeetingsHeader } from "./components/TeamMeetingsHeader";
import { MEETINGS_ACCENT, type MeetingPhase } from "./constants";
import "./teamMeetings.css";

function phaseForMeeting(m: MeetingFull | null | undefined): MeetingPhase {
  if (!m) return "prep";
  if (m.status === "completed" || m.status === "cancelled") return "archive";
  if (m.status === "scheduled" && m.scheduledAt && new Date(m.scheduledAt) <= new Date()) {
    return "summary";
  }
  return "prep";
}

export function TeamMeetingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [phase, setPhase] = useState<MeetingPhase>("prep");
  const [meeting, setMeeting] = useState<MeetingFull | null>(null);
  const [users, setUsers] = useState<AssignableUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [canManage, setCanManage] = useState(false);
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const toast = useCkToast();
  const [archiveAutoOpenId, setArchiveAutoOpenId] = useState<string | null>(null);
  const accent = MEETINGS_ACCENT;

  const showSnack = useCallback(
    (message: string, type: "success" | "error" = "success") => {
      if (type === "error") toast.error(message);
      else toast.success(message);
    },
    [toast],
  );

  const loadUsers = useCallback(async () => {
    try {
      const items = await meetingsApi.listAssignableUsers();
      setUsers(items || []);
    } catch {
      setUsers([]);
    }
  }, []);

  const loadPermissions = useCallback(async () => {
    try {
      const keys = await shellApi.listGrantedPermissions();
      setCanManage(keys.includes("meetings.manage"));
    } catch {
      setCanManage(false);
    }
  }, []);

  const loadCurrent = useCallback(async () => {
    setLoading(true);
    try {
      const meetingParam = searchParams.get("meeting");
      if (meetingParam) {
        try {
          const item = await meetingsApi.getById(meetingParam);
          const nextPhase = phaseForMeeting(item);
          setPhase(nextPhase);
          if (nextPhase === "archive") {
            setMeeting(null);
            setArchiveAutoOpenId(item.id);
          } else {
            setMeeting(item);
            setArchiveAutoOpenId(null);
          }
          return;
        } catch {
          // fall through to current
        }
      }
      setArchiveAutoOpenId(null);
      const current = await meetingsApi.getCurrent();
      setMeeting(current);
      if (current) setPhase(phaseForMeeting(current));
    } catch (err) {
      showSnack(toClientApiError(err).message || "שגיאה בטעינת ישיבות", "error");
    } finally {
      setLoading(false);
    }
  }, [searchParams, showSnack]);

  useEffect(() => {
    void loadUsers();
    void loadPermissions();
  }, [loadUsers, loadPermissions]);

  useEffect(() => {
    void loadCurrent();
  }, [loadCurrent]);

  const handleRefresh = () => {
    void loadCurrent();
  };

  const handleNotify = async () => {
    if (!meeting?.id) return;
    try {
      const res = await meetingsApi.sendReminder(meeting.id);
      showSnack(res?.message || "תזכורות נשלחו");
    } catch (err) {
      showSnack(toClientApiError(err).message || "שגיאה", "error");
    }
  };

  const handleNewMeeting = async (previousMeetingId: string | null = null) => {
    try {
      const item = await meetingsApi.create({
        title: "ישיבת צוות",
        ...(previousMeetingId ? { previousMeetingId } : {}),
      });
      setMeeting(item);
      setPhase("prep");
      setArchiveAutoOpenId(null);
      setSearchParams({});
      showSnack("ישיבה חדשה נוצרה");
    } catch (err) {
      showSnack(toClientApiError(err).message || "שגיאה", "error");
    }
  };

  useQuickCreate("meeting", canManage ? () => void handleNewMeeting() : null);

  const handleResumeMeeting = (m: MeetingFull) => {
    if (!m?.id) return;
    setMeeting(m);
    setArchiveAutoOpenId(null);
    setSearchParams({ meeting: String(m.id) });
    const next = phaseForMeeting(m);
    setPhase(next === "archive" ? "prep" : next);
  };

  const handleFinalized = () => {
    showSnack("סיכום נשמר ונשלח");
    setPhase("archive");
    void loadCurrent();
  };

  const pageStyle = { "--tm-accent": accent } as CSSProperties;

  if (loading) {
    return (
      <div className="tm-loading" style={pageStyle}>
        טוען ישיבות צוות…
      </div>
    );
  }

  return (
    <div className="tm-page" style={pageStyle}>
      <TeamMeetingsHeader
        title="ישיבות צוות"
        accent={accent}
        actions={
          <>
            {canManage ? (
              <Button icon="users" onClick={() => setDefaultsOpen(true)}>
                משתתפים קבועים
              </Button>
            ) : null}
            {!meeting && canManage ? (
              <Button
                variant="primary"
                icon="plus"
                onClick={() => {
                  void handleNewMeeting();
                }}
              >
                ישיבה חדשה
              </Button>
            ) : null}
          </>
        }
      />
      <PhaseNav phase={phase} onChange={setPhase} />
      {phase === "prep" ? (
        <PreparationPanel
          meeting={meeting}
          users={users}
          canManage={canManage}
          onRefresh={handleRefresh}
          onNotify={() => {
            void handleNotify();
          }}
          accent={accent}
        />
      ) : null}
      {phase === "summary" ? (
        <SummaryPanel
          meeting={meeting}
          users={users}
          canManage={canManage}
          onRefresh={handleRefresh}
          onFinalized={handleFinalized}
        />
      ) : null}
      {phase === "archive" ? (
        <ArchivePanel
          canManage={canManage}
          accent={accent}
          onNewMeeting={() => {
            void handleNewMeeting(meeting?.id || archiveAutoOpenId);
          }}
          onCreateFromPrevious={(id) => {
            void handleNewMeeting(id);
          }}
          onResumeMeeting={handleResumeMeeting}
          autoOpenId={archiveAutoOpenId}
          onSnack={showSnack}
        />
      ) : null}
      <DefaultParticipantsDialog
        open={defaultsOpen}
        onClose={() => setDefaultsOpen(false)}
        users={users}
        onSaved={() => showSnack("תבנית משתתפים נשמרה")}
      />
    </div>
  );
}
